/**
 * Period model for the AI usage admin view.
 *
 * The report is loaded for an inclusive range of calendar days, and the
 * header lets the admin pick that range as a month, a quarter, a year, all
 * time, or two dates. Everything here is a plain 'YYYY-MM-DD' / 'YYYY-MM'
 * string, and the date maths runs on Date.UTC so month ends and leap years
 * are exact and no local-timezone shift creeps in. Only "today" comes from
 * the local clock: it is the admin's calendar day.
 *
 * `months` is the API's list of months that have any usage, as
 * `[{ yearMonth }]`, so the selects only ever offer periods with data. When
 * the list is empty (or could not be loaded) the current month stands in.
 */

import { toYearMonth } from './usageSummary';

export const PERIOD_TYPES = ['month', 'quarter', 'year', 'all', 'custom'];

const pad2 = (n) => String(n).padStart(2, '0');

/** The admin's calendar day, 'YYYY-MM-DD'. */
export const todayIsoDate = (now = new Date()) =>
  `${toYearMonth(now)}-${pad2(now.getDate())}`;

const yearMonthOf = (isoDay) => isoDay.slice(0, 7);

const yearOf = (yearMonth) => yearMonth.slice(0, 4);

const quarterOf = (yearMonth) => {
  const [year, month] = yearMonth.split('-').map(Number);
  return `${year}-Q${Math.ceil(month / 3)}`;
};

/** '2026-Q3' → { year: 2026, quarter: 3 }, for the label. */
export const quarterParts = (quarter) => {
  const [year, q] = quarter.split('-Q').map(Number);
  return { year, quarter: q };
};

/** Last day of a 1-based month, via day 0 of the month after it. */
const lastDayOfMonth = (year, month) =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

const monthRange = (yearMonth) => {
  const [year, month] = yearMonth.split('-').map(Number);
  return {
    from: `${yearMonth}-01`,
    to: `${yearMonth}-${pad2(lastDayOfMonth(year, month))}`,
  };
};

const quarterRange = (quarter) => {
  const { year, quarter: q } = quarterParts(quarter);
  const first = (q - 1) * 3 + 1;
  const last = first + 2;
  return {
    from: `${year}-${pad2(first)}-01`,
    to: `${year}-${pad2(last)}-${pad2(lastDayOfMonth(year, last))}`,
  };
};

const yearRange = (year) => ({ from: `${year}-01-01`, to: `${year}-12-31` });

const unique = (values) => Array.from(new Set(values));

/** The months with data, newest first, or the current month when there are none. */
export const monthOptions = (months, today) => {
  const list = unique((months || []).map((month) => month.yearMonth))
    .sort()
    .reverse();
  return list.length ? list : [yearMonthOf(today)];
};

export const quarterOptions = (months, today) =>
  unique(monthOptions(months, today).map(quarterOf));

export const yearOptions = (months, today) =>
  unique(monthOptions(months, today).map(yearOf));

export const defaultMonth = (months, today) => monthOptions(months, today)[0];

/**
 * The newest choice for a period type: the month with the latest data, the
 * quarter or year holding it, or the current month so far for a custom range.
 */
export const defaultPeriodValue = (type, months, today) => {
  const newest = defaultMonth(months, today);
  switch (type) {
    case 'month':
      return newest;
    case 'quarter':
      return quarterOf(newest);
    case 'year':
      return yearOf(newest);
    case 'custom':
      return { from: `${yearMonthOf(today)}-01`, to: today };
    default:
      return null;
  }
};

/** The inclusive day range a period resolves to. */
export const rangeFor = (type, value, { months, today }) => {
  switch (type) {
    case 'month':
      return monthRange(value);
    case 'quarter':
      return quarterRange(value);
    case 'year':
      return yearRange(value);
    case 'all': {
      const list = monthOptions(months, today);
      return { from: `${list[list.length - 1]}-01`, to: today };
    }
    case 'custom':
      return { from: value.from, to: value.to };
    default:
      throw new Error(`Unknown period type: ${type}`);
  }
};

export const rangeKey = ({ from, to }) => `${from}..${to}`;

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar day as 'YYYY-MM-DD' — '2026-02-30' is not one. */
export const isIsoDay = (value) => {
  const match = typeof value === 'string' && value.match(ISO_DAY);
  if (!match) return false;
  const [, year, month, day] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

/** A range the API accepts: two real days, in order. */
export const isValidRange = (range) =>
  Boolean(range) &&
  isIsoDay(range.from) &&
  isIsoDay(range.to) &&
  range.from <= range.to;
